import React, { useEffect, useRef, useState } from "react";
import { Col, Row, Button, Table, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import axios from "axios";
import baseURL from "../../../../../../Url/NodeBaseURL"

const PaymentDetailsV2 = ({
  // Core data
  estimate_number,
  
  // Amounts and calculations
  totalAmount,
  taxableAmount,
  discountAmt,
  taxAmount,
  netAmount,
  oldItemsAmount,
  schemeAmount,
  salesAmountToPass,
  netPayableAmount,
  
  // Payment state
  paymentDetails,
  setPaymentDetails,
  
  // UI state
  saving,
  loading,
  festivalShowModal,
  
  // Handlers
  handleSave,
  handleBack,
  handleFestivalShowModal,
  handleFestivalCloseModal,
  handleApply,
  
  // Festival offers
  offers,
  appliedOffers,
  setAppliedOffers,
  festivalDiscountAmt,
  isAnyOfferApplied,
  
  // Discount handling
  discount,
  handleDiscountChange,
  
  // Manual amount handling
  handleManualNetPayAmountChange,
  isManualNetMode = false,
  manualNetPayAmount = 0,
  
  // Form reset function
  resetAllFormData,
}) => {
  const navigate = useNavigate();
  
  // Simplified state - only what's needed for editing
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(() => {
    // Initialize with a safe value
    const initialValue = Math.round(netPayableAmount || 0);
    console.log('[PaymentDetailsV2] Initializing editValue:', initialValue);
    return initialValue;
  });
  // Use a small counter to ignore both sync effects after a manual save
  const ignoreSyncCountRef = useRef(0);
  const userEditingRef = useRef(false);
  const prevURDSRRef = useRef({ oldItemsAmount: 0, salesAmountToPass: 0 });

  //disabling generate sales when a bill is already created
  const [generateSales, setGenerateSales] = useState(true);
  const checkEstimateDetails = async (estimate_number) => {
    try {
      const response = await axios.get(
        `${baseURL}/Estimate/estimate/${estimate_number}`
      );
      console.log("response is", response.data)
      
      if (response.data.repair_details_id) {
        setGenerateSales(false);
      } else {
        setGenerateSales(true);
      }
      console.log("generate sales", generateSales)
    } catch (error) {
      console.error('Error fetching estimate details:', error);
    }
  };
  useEffect(() => {
    if (estimate_number) {
      checkEstimateDetails(estimate_number);
    }
  }, [estimate_number]);
  useEffect(() => {
  }, [generateSales]);

  
  // Update edit value when amounts change (respect manual mode when not editing)
  useEffect(() => {
    if (ignoreSyncCountRef.current > 0) {
      // Skip sync cycles right after manual save to avoid flicker/override
      ignoreSyncCountRef.current -= 1;
      return;
    }
    
    // Don't auto-update if user is actively editing
    if (userEditingRef.current) {
      return;
    }
    
    // Don't auto-update if we're in edit mode (let the other useEffect handle it)
    if (isEditing) {
      return;
    }
    
    // Use the currently displayed amount: manual amount in manual mode, computed otherwise
    const displayedNetPayable = Math.round((isManualNetMode ? manualNetPayAmount : netPayableAmount) || 0);
    
    // Only update if there's a significant difference to avoid unnecessary re-renders
    // Also add a sanity check to prevent extreme differences (like missing/extra digits)
    const difference = Math.abs(displayedNetPayable - editValue);
    if (difference > 1 && difference < 1000000) { // Sanity check: difference should be reasonable
      console.log('[PaymentDetailsV2] Updating editValue to match netPayableAmount:', {
        oldEditValue: editValue,
        newEditValue: displayedNetPayable,
        netPayableAmount: displayedNetPayable,
        difference
      });
      setEditValue(displayedNetPayable);
    } else if (difference >= 1000000) {
      console.warn('[PaymentDetailsV2] Suspicious difference detected, skipping update:', {
        oldEditValue: editValue,
        newEditValue: displayedNetPayable,
        difference
      });
    }
  }, [netPayableAmount, manualNetPayAmount, isManualNetMode, editValue, isEditing]);

  // Consolidated logic to enable editing mode
  useEffect(() => {
    // Don't change edit mode if we're ignoring syncs (post-save)
    if (ignoreSyncCountRef.current > 0) {
      ignoreSyncCountRef.current -= 1;
      return;
    }

    // Don't auto-enable edit mode if user is actively editing
    if (userEditingRef.current) {
      return;
    }

    let shouldEnableEditing = false;
    let shouldUpdateEditValue = false;
    // Determine what value is currently shown in the UI
    const currentDisplayValue = Math.round((isManualNetMode ? manualNetPayAmount : netPayableAmount) || 0);

    // Case 1: Manual mode is active AND there are actual changes that need attention
    // Don't auto-enable edit mode just because manual mode is active
    if (isManualNetMode && Math.abs(currentDisplayValue - editValue) > 1) {
      shouldEnableEditing = true;
      shouldUpdateEditValue = true;
      console.log('[PaymentDetailsV2] Manual mode active with changes, enabling edit mode');
    }
    
    // Case 2: URD purchases or sales returns amounts changed
    // Only enable edit mode when URD/SR amounts actually change, not just because they exist
    const urdSRChanged = Math.abs(oldItemsAmount - prevURDSRRef.current.oldItemsAmount) > 0.01 || 
                        Math.abs(salesAmountToPass - prevURDSRRef.current.salesAmountToPass) > 0.01;
    
    if (urdSRChanged) {
      shouldEnableEditing = true;
      shouldUpdateEditValue = true;
      console.log('[PaymentDetailsV2] URD/SR amounts changed, enabling edit mode:', {
        oldURD: prevURDSRRef.current.oldItemsAmount,
        newURD: oldItemsAmount,
        oldSR: prevURDSRRef.current.salesAmountToPass,
        newSR: salesAmountToPass
      });
      // Update the previous values
      prevURDSRRef.current = { oldItemsAmount, salesAmountToPass };
    }
    
    // Case 3: Net payable amount changed significantly (e.g., after adding products)
    else if (Math.abs(currentDisplayValue - editValue) > 1) {
      shouldEnableEditing = true;
      shouldUpdateEditValue = true;
      console.log('[PaymentDetailsV2] Net payable amount changed significantly, enabling edit mode:', {
        netPayableAmount: currentDisplayValue,
        editValue,
        difference: Math.abs(currentDisplayValue - editValue)
      });
    }

    // Apply the changes
    if (shouldEnableEditing && !isEditing) {
      console.log('[PaymentDetailsV2] Enabling edit mode');
      setIsEditing(true);
    }
    
    // Update editValue when enabling edit mode to show the current amount
    if (shouldUpdateEditValue && shouldEnableEditing) {
      // When enabling edit mode, prefill with what's currently displayed to the user
      const newEditValue = Math.round(currentDisplayValue || 0);
      console.log('[PaymentDetailsV2] Updating editValue when enabling edit mode:', {
        oldEditValue: editValue,
        newEditValue,
        netPayableAmount: currentDisplayValue
      });
      setEditValue(newEditValue);
      // Reset user editing flag since this is an automatic update
      userEditingRef.current = false;
    }
  }, [netPayableAmount, manualNetPayAmount, isManualNetMode, isEditing, oldItemsAmount, salesAmountToPass, editValue]);



  const handleClose = () => {
    navigate(-1);
  };

  const handleNew = () => {
    console.log('[PaymentDetailsV2] New button clicked - resetting all form data');
    
    // Use the comprehensive reset function from parent component
    if (resetAllFormData) {
      resetAllFormData();
      
      // Navigate to the estimates v2 route to ensure clean state
      try {
        navigate(`/estimates/v2`, { replace: true });
      } catch (e) {
        console.error('[PaymentDetailsV2] Navigation error:', e);
      }
    } else {
      console.warn('[PaymentDetailsV2] resetAllFormData function not provided, falling back to page reload');
      // Fallback to page reload if reset function is not available
      try {
        navigate(`/estimates/v2`, { replace: true });
      } catch (e) {}
      setTimeout(() => {
        window.location.reload();
      }, 50);
    }
  };

  // Simple edit toggle
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    // Reset user editing flag when toggling edit mode
    userEditingRef.current = false;
  };

  // When user clicks Save, propagate the value via parent handler
  const handleSaveNetPayableAmount = () => {
    console.log('[PaymentDetailsV2] Save attempt started:', {
      editValue,
      rawEditValue: editValue,
      netAmount,
      oldItemsAmount,
      salesAmountToPass,
      netPayableAmount,
      isManualNetMode,
      handleManualNetPayAmountChange: !!handleManualNetPayAmountChange
    });

    const newValue = parseFloat(editValue);

    if (isNaN(newValue)) {
      console.log('[PaymentDetailsV2] Invalid value - NaN');
      alert("Please enter a valid net payable amount.");
      return;
    }

    // Validate against the current computed max based on totals
    // Max allowed = current net amount - URD - Sales Return (can be negative if URD/SR exceed net amount)
    const expectedNetPayable = netAmount - oldItemsAmount - salesAmountToPass + discountAmt;

    // Prevent user from entering an amount greater than the rounded expected payable
    const expectedRounded = Math.round(expectedNetPayable);

    console.log('[PaymentDetailsV2] Validation check:', {
      newValue,
      expectedNetPayable,
      expectedRounded,
      willExceed: newValue > expectedRounded
    });

    if (newValue > expectedRounded) {
      console.log('[PaymentDetailsV2] Value exceeds maximum allowed');
      alert(`Net payable amount cannot exceed ${expectedRounded}. Please enter a valid amount.`);
      // Reset the input to the current net payable amount
      setEditValue(Math.round(netPayableAmount || 0));
      return;
    }

    // Update the parent component (can be negative; discount will be recomputed accordingly)
    const roundedValue = Math.round(newValue);
    console.log('[PaymentDetailsV2] Calling parent handler:', {
      roundedValue,
      hasHandler: !!handleManualNetPayAmountChange
    });

    if (handleManualNetPayAmountChange) {
      handleManualNetPayAmountChange(roundedValue);
      console.log('[PaymentDetailsV2] Parent handler called successfully');
    } else {
      console.log('[PaymentDetailsV2] ERROR: No parent handler available');
    }

    // Exit edit mode
    setIsEditing(false);
    // Preserve the user-entered value visually until the recalculation settles
    setEditValue(roundedValue);
    // Ignore the next two sync cycles (both effects) to prevent immediate override
    ignoreSyncCountRef.current = 2;
    // Reset user editing flag
    userEditingRef.current = false;
    
    console.log('[PaymentDetailsV2] Save completed, exiting edit mode');
  };

  return (
    <>
      <div>
        <Col className="sales-form-section">
          <Row>
            <h4 className="mb-3" style={{ fontSize: "20px" }}>
              Summary
            </h4>
            <Table bordered hover responsive>
              <tr style={{ fontSize: "13px" }}>
                <td colSpan="16" className="text-right">
                  Total Amount
                </td>
                <td colSpan="4">{totalAmount.toFixed(2)}</td>
              </tr>
              <tr style={{ fontSize: "13px" }}>
                <td colSpan="12" className="text-right">
                  Discount Amounts
                </td>
                <td colSpan="4">
                  {" "}
                  @ {discount ? discount.toFixed(2) : 0} %
                  {/* <input
                    type="number"
                    value={discount ? discount.toFixed(2) : 0}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      if (inputValue === '' || inputValue === null) {
                        handleDiscountChange({ target: { value: 0 } });
                        return;
                      }
                      const newValue = parseFloat(inputValue) || 0;
                      if (newValue < 0) {
                        alert("Discount cannot be negative. Please enter a valid amount.");
                        handleDiscountChange({ target: { value: discount ? discount.toFixed(2) : 0 } });
                        return;
                      }
                      handleDiscountChange(e);
                    }}
                    max="100"
                    min="0"
                    step="0.01"
                    style={{ width: "70px", padding: "2px", fontSize: "13px" }}
                  /> */}
                </td>
                <td colSpan="4">{discountAmt.toFixed(2)}</td>
              </tr>
              <tr style={{ fontSize: "13px" }}>
                <td colSpan="12" className="text-right">
                  Festival Offer
                </td>
                <td colSpan="4"> &ensp;&ensp;</td>
                <td colSpan="4">
                  {festivalDiscountAmt.toFixed(2)}
                  <Button
                    onClick={handleFestivalShowModal}
                    style={{
                      display: offers && offers.length > 0 ? "inline-block" : "none",
                      backgroundColor: "#a36e29",
                      borderColor: "#a36e29",
                      fontSize: "12px",
                      padding: "2px 8px",
                      marginLeft: "10px",
                    }}
                    size="sm"
                  >
                    Apply Offer
                  </Button>
                </td>
              </tr>
              <tr style={{ fontSize: "13px" }}>
                <td colSpan="16" className="text-right">
                  Taxable Amount
                </td>
                <td colSpan="4">{taxableAmount.toFixed(2)}</td>
              </tr>
              <tr style={{ fontSize: "13px" }}>
                <td colSpan="16" className="text-right">
                  Tax Amount
                </td>
                <td colSpan="4">{taxAmount.toFixed(2)}</td>
              </tr>
              <tr style={{ fontSize: "13px" }}>
                <td colSpan="16" className="text-right">
                  Net Amount
                </td>
                <td colSpan="4">{netAmount.toFixed(2)}</td>
              </tr>
              <tr style={{ fontSize: "13px" }}>
                <td colSpan="16" className="text-right">
                  Old Items Amount
                </td>
                <td colSpan="4">{oldItemsAmount.toFixed(2)}</td>
              </tr>
              <tr style={{ fontSize: "13px" }}>
                <td colSpan="16" className="text-right">
                  Scheme Amount
                </td>
                <td colSpan="4">{schemeAmount.toFixed(2)}</td>
              </tr>
              <tr style={{ fontSize: "13px" }}>
                <td colSpan="16" className="text-right">
                  Sale Return Amount
                </td>
                <td colSpan="4">{salesAmountToPass.toFixed(2)}</td>
              </tr>

              <tr style={{ fontSize: "13px" }}>
                <td colSpan="16" className="text-right">
                  Net Payable Amount
                </td>
                {isEditing && generateSales ? (
                  <td colSpan="4">
                    <input
                      type="number"
                      style={{
                        width: "100px",
                        padding: "5px",
                        fontSize: "13px",
                      }}
                      value={editValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Mark that user is actively editing
                        userEditingRef.current = true;
                        
                        // Clear the timeout if it exists
                        if (window.userEditingTimeout) {
                          clearTimeout(window.userEditingTimeout);
                        }
                        
                        // Allow empty string for backspace/clearing
                        if (value === '' || value === '-') {
                          setEditValue(0); // Set to 0 instead of string
                        } else {
                          // Convert to number for valid inputs
                          const numValue = parseFloat(value);
                          if (!isNaN(numValue)) {
                            // Force integer value (rounded) per business rule
                            setEditValue(Math.round(numValue));
                          }
                        }
                        
                        // Reset user editing flag after 2 seconds of no typing (increased from 1 second)
                        window.userEditingTimeout = setTimeout(() => {
                          console.log('[PaymentDetailsV2] User editing timeout expired, allowing auto-updates');
                          userEditingRef.current = false;
                        }, 2000);
                      }}
                      step="1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSaveNetPayableAmount();
                        }
                      }}
                    />
                    {generateSales &&(
                    <Link onClick={handleSaveNetPayableAmount}>
                      Save
                    </Link>
                    )}
                  </td>
                ) : (
                  <td colSpan="4">
                    {/* In manual mode, show the manual amount; otherwise show calculated amount */}
                    {isManualNetMode ? 
                      Math.round(manualNetPayAmount || 0) : 
                      Math.round(netPayableAmount || 0)
                    }
                    {generateSales &&(
                    <FontAwesomeIcon
                      icon={faPencil}
                      onClick={handleEditToggle}
                      style={{
                        cursor: "pointer",
                        color: "black",
                        backgroundColor: "transparent",
                        marginLeft: "5px",
                      }}
                    />
                    )}
                  </td>
                )}
              </tr>
            </Table>
          </Row>
        </Col>

        {/* Action Buttons */}
        <Row className="align-items-center mt-2">
          {generateSales && (
          <Col xs={12} md="auto">
            <Button
              onClick={async () => {
                // If net payable is in edit mode, save it first
                if (isEditing) {
                  console.log('[PaymentDetailsV2] Save and Print clicked while in edit mode, saving net payable first');
                  handleSaveNetPayableAmount();
                }
                // Then proceed with the main save
                await handleSave();
                
                // After successful save, reset the form for new estimate
                console.log('[PaymentDetailsV2] Save completed, resetting form for new estimate');
                
                // Wait a moment for save to complete and print to start, then reset
                setTimeout(() => {
                  if (resetAllFormData) {
                    resetAllFormData();
                    console.log('[PaymentDetailsV2] Form reset completed after save and print');
                  } else {
                    console.warn('[PaymentDetailsV2] resetAllFormData function not provided, falling back to page reload');
                    // Fallback to page reload if reset function is not available
                    sessionStorage.setItem('createNewEstimate', 'true');
                    window.location.reload();
                  }
                }, 1000); // Wait 1 second for save to complete and print to start
              }}
              disabled={saving || isEditing}
              style={{
                backgroundColor: "#a36e29",
                borderColor: "#a36e29",
                fontSize: "14px",
                padding: "4px 8px",
              }}
            >
              {saving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving...
                </>
              ) : (
                "Save and Print"
              )}
            </Button>
          </Col>
          )}
          {generateSales && (
          <Col xs={12} md="auto">
            <Button
              onClick={async () => {
                try {
                  // First save the estimate without triggering print/reset
                  await handleSave(true);
                  // If save is successful, navigate to sales
                  navigate("/sales", {
                    state: {
                      fromEstimate: true,
                      estimateNumber: estimate_number,
                    },
                  });
                } catch (error) {
                  console.error('Error saving estimate before generating sales:', error);
                  // Don't navigate if save fails
                }
              }}
              style={{
                backgroundColor: "gray",
                borderColor: "gray",
                fontSize: "14px",
                padding: "4px 10px",
              }}
              disabled={saving || !generateSales || isEditing}
            >
              {saving ? "Saving..." : "Generate Sales"}
            </Button>
          </Col>
          )}
          <Col xs={12} md="auto">
            <Button
              onClick={handleClose}
              style={{
                backgroundColor: "gray",
                borderColor: "gray",
                fontSize: "14px",
                padding: "4px 10px",
              }}
            >
              Close
            </Button>
          </Col>

          <Col xs={12} md="auto">
            <Button
              onClick={handleNew}
              style={{
                backgroundColor: "gray",
                borderColor: "gray",
                fontSize: "14px",
                padding: "4px 10px",
              }}
            >
              New
            </Button>
          </Col>
        </Row>
      </div>

      {/* Festival Offers Modal */}
      <Modal
        show={festivalShowModal}
        onHide={handleFestivalCloseModal}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Available Offers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <p>Loading offers...</p>
          ) : offers.length > 0 ? (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>S No</th>
                  <th>Offer Name</th>
                  <th>Discount On Rate</th>
                  <th>Discount % on MC</th>
                  <th>Discount % for Fixed</th>
                  <th>Valid From</th>
                  <th>Valid To</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {offers.map((offer, index) => {
                  const offerKey = `${offer._id}_${index}`;
                  const isApplied = appliedOffers[offerKey] || false;

                  const formatDate = (dateStr) => {
                    const date = new Date(dateStr);
                    return `${date.getDate().toString().padStart(2, "0")}-${(
                      date.getMonth() + 1
                    )
                      .toString()
                      .padStart(2, "0")}-${date.getFullYear()}`;
                  };

                  return (
                    <tr key={offer._id}>
                      <td>{index + 1}</td>
                      <td>{offer.offer_name}</td>
                      <td>{offer.discount_on_rate}</td>
                      <td>{offer.discount_percentage}</td>
                      <td>{offer.discount_percent_fixed}</td>
                      <td>{formatDate(offer.valid_from)}</td>
                      <td>{formatDate(offer.valid_to)}</td>
                      <td>
                        <Button
                          variant={isApplied ? "danger" : "success"}
                          size="sm"
                          onClick={() => handleApply(offer, index)}
                        >
                          {isApplied ? "Unapply" : "Apply"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : (
            <p>No offers available at the moment.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleFestivalCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PaymentDetailsV2; 